// Knopfleiste fest unten (Handoff §6): links ein grauer Knopf („Abbrechen“, „Zurück“ …, nur wenn der Code
// einen hat), rechts der Hauptknopf (Gold-Verlauf, flex 1). Gesperrt = grau und nicht klickbar, Tooltip
// nennt den Grund. Beim Speichern dreht ein Ladekreis. Beschriftungen exakt wie im Code.
// Sitzt `position: sticky` am Ende des Scrollbereichs – so bleibt der Hauptknopf im <form> (Enter sendet).
import type { ReactNode } from 'react';
import { CircleNotch, type Icon } from '@phosphor-icons/react';
import { knopfGesperrt } from './form-logik';
import { KNOPF } from './form-stil';

export interface HauptKnopf {
  label: string;
  icon?: Icon;
  /** Icon rechts vom Text (z. B. Pfeil bei „Vorschau“) */
  iconRechts?: boolean;
  /** submit = sendet das umgebende <form> (Standard) */
  typ?: 'submit' | 'button';
  onClick?: () => void;
  laedt?: boolean;
  /** Beschriftung während des Speicherns – nur wenn der Code eine eigene hat. */
  ladeLabel?: string;
  gesperrt?: boolean;
  /** Tooltip, warum der Knopf gesperrt ist. */
  gesperrtGrund?: string;
  art?: 'gold' | 'gefahr';
  tip?: string;
  /** Nur das Icon zeigen (schmale Breiten); die Beschriftung bleibt für Screenreader. */
  nurIcon?: boolean;
}

/** Grauer Knopf links (Abbrechen, Zurück, Vorschau …). */
export interface NebenKnopf {
  label: string;
  onClick: () => void;
  icon?: Icon;
  tip?: string;
  laedt?: boolean;
  gesperrt?: boolean;
  /** Teilt sich die Breite mit dem Hauptknopf (flex 1) statt schmal zu bleiben. */
  breit?: boolean;
}

export interface FormKnopfleisteProps {
  abbrechen?: NebenKnopf;
  haupt?: HauptKnopf;
  /** Weitere Knöpfe zwischen den beiden (z. B. Löschen). */
  zusatz?: ReactNode;
  /** Inhalt ganz links, der den freien Platz einnimmt (z. B. Werkzeug-Dock). */
  links?: ReactNode;
  /** Schwebendes Element über der Leiste (z. B. Popover); positioniert sich selbst. */
  ueber?: ReactNode;
}

const GRUND = { height: KNOPF.hoehe, padding: '0 18px', borderRadius: KNOPF.radius, gap: 8, font: 'inherit', fontSize: 14, fontWeight: 800 } as const;

export function FormKnopfleiste({ abbrechen, haupt, zusatz, links, ueber }: FormKnopfleisteProps) {
  return (
    <div className="sticky bottom-0 z-10 flex" style={{
      gap: 8, padding: '12px 16px 14px', borderTop: `0.5px solid ${KNOPF.leisteLinie}`,
      background: `${KNOPF.leiste}, ${KNOPF.leisteGrund}`, boxShadow: KNOPF.leisteSchatten,
    }}>
      {ueber}
      {links && <div className="flex min-w-0 flex-1 items-center">{links}</div>}
      {abbrechen && <Neben {...abbrechen} />}
      {zusatz}
      {haupt && <Haupt {...haupt} />}
    </div>
  );
}

function Neben({ label, onClick, icon, tip, laedt, gesperrt, breit }: NebenKnopf) {
  const zu = knopfGesperrt({ gesperrt, laedt });
  const stil = gesperrt ? KNOPF.gesperrt : KNOPF.grau;
  const I = laedt ? CircleNotch : icon;
  return (
    <button type="button" onClick={onClick} disabled={zu} aria-busy={laedt || undefined} data-tip={tip}
      className={`pf-knopf flex items-center justify-center whitespace-nowrap border-none ${breit ? 'min-w-0 flex-1' : 'shrink-0'}`}
      style={{ ...GRUND, background: stil.flaeche, color: stil.text, cursor: gesperrt ? 'not-allowed' : laedt ? 'progress' : 'pointer' }}>
      {I && <I aria-hidden size={15} weight="bold" className={laedt ? 'pille-dreh shrink-0' : 'shrink-0'} />}
      <span className="truncate">{label}</span>
    </button>
  );
}

function Haupt(haupt: HauptKnopf) {
  const gesperrt = knopfGesperrt(haupt);
  const stil = haupt.gesperrt ? KNOPF.gesperrt : haupt.art === 'gefahr' ? KNOPF.gefahr : KNOPF.gold;
  const I = haupt.laedt ? CircleNotch : haupt.icon;
  const icon = I && <I aria-hidden size={15} weight="bold" className={haupt.laedt ? 'pille-dreh shrink-0' : 'shrink-0'} />;
  const rechts = haupt.iconRechts && !haupt.laedt;
  return (
    <button type={haupt.typ ?? 'submit'} onClick={haupt.onClick} disabled={gesperrt} aria-busy={haupt.laedt || undefined}
      aria-label={haupt.nurIcon ? haupt.label : undefined}
      data-tip={haupt.gesperrt ? haupt.gesperrtGrund : haupt.tip}
      className={`pf-knopf flex items-center justify-center whitespace-nowrap border-none ${haupt.nurIcon ? 'shrink-0' : 'min-w-0 flex-1'}`}
      style={{
        ...GRUND, ...(haupt.nurIcon ? { width: KNOPF.hoehe, padding: 0 } : {}), background: stil.flaeche, color: stil.text, boxShadow: stil.schatten,
        cursor: haupt.gesperrt ? 'not-allowed' : haupt.laedt ? 'progress' : 'pointer',
      }}>
      {!rechts && icon}
      {!haupt.nurIcon && <span className="truncate">{haupt.laedt && haupt.ladeLabel ? haupt.ladeLabel : haupt.label}</span>}
      {rechts && icon}
    </button>
  );
}
